import { supabase } from '@/integrations/supabase/client';
import {
  BracketEntry,
  BracketPicks,
  GlobalLeaderboardEntry,
  BRACKET_DEADLINE,
  BRACKET_OPEN_DATE,
} from '@/types/bracket';

// ── Display name resolution ──────────────────────────────────────────────────

/**
 * Resolves a display name for a user.
 * Strategy:
 *   1. auth.getUser() → user_metadata.full_name
 *   2. Fallback: group_members most recent display_name for this user
 *   3. Fallback: email prefix (before @) from auth user
 *   4. Last resort: 'שחקן' (player in Hebrew)
 */
export async function resolveDisplayName(userId: string): Promise<string> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (user && user.id === userId) {
      const fullName = user.user_metadata?.full_name as string | undefined;
      if (fullName && fullName.trim()) return fullName.trim();

      const email = user.email;
      if (email) {
        const emailPrefix = email.split('@')[0];
        if (emailPrefix) return emailPrefix;
      }
    }
  } catch {
    // Continue to fallbacks
  }

  // Fallback: group_members display_name (most recent)
  try {
    const { data: member } = await supabase
      .from('group_members')
      .select('display_name')
      .eq('user_id', userId)
      .order('joined_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (member?.display_name) return member.display_name;
  } catch {
    // Continue to last resort
  }

  return 'שחקן';
}

// ── isBracketLocked ──────────────────────────────────────────────────────────

/**
 * Pure function — returns true if current time is at or after the bracket open date.
 */
export function isBracketOpen(): boolean {
  return new Date() >= new Date(BRACKET_OPEN_DATE);
}

/**
 * Pure function — returns true if current time is at or after the bracket deadline.
 */
export function isBracketLocked(): boolean {
  return new Date() >= new Date(BRACKET_DEADLINE);
}

// ── fetchBracketEntry ────────────────────────────────────────────────────────

/**
 * Fetches the authenticated user's own bracket entry.
 * Returns null if no bracket exists yet.
 */
export async function fetchBracketEntry(userId: string): Promise<BracketEntry | null> {
  const { data, error } = await supabase
    .from('bracket_entries')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw new Error(`שגיאה בטעינת הסוגר: ${error.message}`);
  if (!data) return null;

  return data as unknown as BracketEntry;
}

// ── saveBracketEntry ─────────────────────────────────────────────────────────

/**
 * Upserts (insert or update) the user's bracket entry.
 * Uses onConflict: 'user_id' so repeated saves update the existing row.
 * The displayName parameter must be pre-resolved by the caller using resolveDisplayName().
 */
export async function saveBracketEntry(
  userId: string,
  picks: BracketPicks,
  oddsSnapshot: Record<string, unknown>,
  displayName: string,
): Promise<BracketEntry> {
  const { data, error } = await supabase
    .from('bracket_entries')
    .upsert(
      {
        user_id: userId,
        display_name: displayName,
        picks: picks as unknown as Record<string, unknown>,
        odds_snapshot: oddsSnapshot,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    )
    .select()
    .single();

  if (error) throw new Error(`שגיאה בשמירת הסוגר: ${error.message}`);
  if (!data) throw new Error('שגיאה בשמירת הסוגר: אין נתונים');

  return data as unknown as BracketEntry;
}

// ── fetchAllBracketEntries ───────────────────────────────────────────────────

/**
 * Fetches all bracket entries.
 * RLS enforces visibility: only returns all entries after the deadline.
 * Before deadline, each user can only see their own.
 */
export async function fetchAllBracketEntries(): Promise<BracketEntry[]> {
  const { data, error } = await supabase
    .from('bracket_entries')
    .select('*')
    .order('total_points', { ascending: false });

  if (error) throw new Error(`שגיאה בטעינת הסוגרים: ${error.message}`);

  return (data || []) as unknown as BracketEntry[];
}

// ── fetchGlobalLeaderboard ───────────────────────────────────────────────────

/**
 * Fetches the global leaderboard ordered by rank ascending.
 */
export async function fetchGlobalLeaderboard(): Promise<GlobalLeaderboardEntry[]> {
  const { data, error } = await supabase
    .from('global_leaderboard')
    .select('*')
    .order('rank', { ascending: true });

  if (error) throw new Error(`שגיאה בטעינת הטבלה הגלובלית: ${error.message}`);

  return (data || []) as unknown as GlobalLeaderboardEntry[];
}
